import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@core/services/core/auth/auth.service';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { isAndroidApp } from '@utils/utils';
import * as moment from 'moment';
import { BehaviorSubject, from, Subject, throwError } from 'rxjs';
import { catchError, flatMap, map } from 'rxjs/operators';
import { Environment, MMobileService } from '../../../core/services/common';
import { LANGUAGE, SMS } from '../../../modules/receipt-payment/receipt-payment-constants';
import {
  AvailableSinister,
  Brand,
  Intervention,
  ObjectDamaged,
  PatrimonialIdentification,
  PatrimonialSinister,
  PatrimonialUserValid,
  SinisterDamage
} from '../../models/patrimonial';
import { Policy } from '../../models/policy';
import { PolicyHome } from '../../models/policy-home';
import { PolicyOther } from '../../models/policy-other';
import { PatrimonialContextService } from '../context/patrimonial-context.service';
import { PoliciesContextService } from '../context/policies-context.service';
import { BaseService } from '../core/base.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';

@Injectable({
  providedIn: 'root'
})
export class PatrimonialService {
  defaultIconSrc = 'assets/images/patrimonials/description.png';
  availableSinistersLocalData = {
    TRA: { iconSrc: 'assets/images/patrimonials/todo-riesgo.svg' },
    RE: {
      iconSrc: 'assets/images/patrimonials/reparacion-electrodomesticos.svg'
    },
    FA: { iconSrc: 'assets/images/patrimonials/fenomenos-atmosfericos.svg' },
    R: { iconSrc: 'assets/images/patrimonials/robo.svg' },
    DT: { iconSrc: 'assets/images/patrimonials/responsabilidad-civil.svg' },
    JUR: { iconSrc: 'assets/images/patrimonials/orientacion-juridica.svg' },
    A: { iconSrc: 'assets/images/patrimonials/danos-agua.svg' },
    C: { iconSrc: 'assets/images/patrimonials/roturas.svg' },
    IR: { iconSrc: 'assets/images/patrimonials/intento-robo.svg' },
    ELL: { iconSrc: 'assets/images/patrimonials/extravio-llaves.svg' },
    DE: { iconSrc: 'assets/images/patrimonials/danos-electricos.svg' },
    BH: { iconSrc: 'assets/images/patrimonials/bricolaje-hogar.svg' },
    DM: { iconSrc: 'assets/images/patrimonials/bricolaje-hogar.svg' },
    AI: { iconSrc: 'assets/images/patrimonials/asistencia-informatica.svg' }
  };

  availableSinistersData: {};

  publicPatrimonialsAppKey = 'mets-patrimonials-sms';
  identificationRetriesCounterTimeStamp = '';
  identificationRetriesBlock = 60;
  isPublicPatrimonialProcess$ = new Subject<boolean>();
  origin$ = new BehaviorSubject<string>('');

  private ENTITY_CODE = 'roEntityCode';
  private GROUPING_CODE = 'roGroupingCode';
  private ORIGIN = 'roOrigin';
  private PRODUCT_CODE = 'roProductCode';
  private PRODUCT_ID = 'roProductId';

  constructor(
    private http: HttpClient,
    protected mmobile: MMobileService,
    protected base: BaseService,
    private or: OperationRegisterService,
    private mmobileHelper: MMobileHelperService,
    private policiesCtx: PoliciesContextService,
    private patrimonialContext: PatrimonialContextService,
    private storage: Storage,
    private platform: Platform,
    public auth: AuthService,
  ) {}

  getSinisterTypesForDate(
    policy: Policy,
    date: string,
    isPrivateArea: boolean
  ) {
    this.loadDataFromMMobile();
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `availableSinisters?date=${date}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      null,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http
      .get(url, options)
      .pipe(
        map(res =>
          (res as AvailableSinister[])
            .map(availableSinister =>
              this.mergeAvailableSinistersData(availableSinister)
            )
            .map(availableSinister =>
              this.mergeAvailableSinistersLocalData(availableSinister)
            )
        )
      );
  }

  getSinisterDamages(
    policy: Policy,
    date: string,
    availableSinister: AvailableSinister,
    isPrivateArea: boolean
  ) {
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `damages?date=${date}&sinisterType=${availableSinister.type}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      availableSinister.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http.get<SinisterDamage[]>(url, options);
  }

  getActivities({ policyNumber }: Policy, isPrivateArea: boolean) {
    const url = this.setUrl(isPrivateArea, policyNumber, 'activities');
    const headers = this.setHeaders(isPrivateArea);
    return this.http.get<SinisterDamage[]>(url, headers);
  }

  getHomeDiyDamages(
    policy: Policy,
    date: string,
    availableSinister: AvailableSinister,
    isPrivateArea: boolean
  ) {
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `damages/homeDIY?date=${date}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      availableSinister.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http
      .get(url, options)
      .pipe(map((res: any) => res.activities as SinisterDamage[]));
  }

  getAtmosphericPhenomenaDamages(
    policy: Policy,
    date: string,
    availableSinister: AvailableSinister,
    isPrivateArea: boolean
  ) {
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `damages/atmosphericPhenomena?date=${date}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      availableSinister.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http
      .get(url, options)
      .pipe(map(res => res as SinisterDamage[]));
  }

  getAllRiskDamages(
    policy: Policy,
    date: string,
    availableSinister: AvailableSinister,
    isPrivateArea: boolean
  ) {
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `damages/allRisk?date=${date}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      availableSinister.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http.get(url, options).pipe(map(res => res as ObjectDamaged[]));
  }

  getBrands(
    policy: Policy,
    availableSinister: AvailableSinister,
    isPrivateArea: boolean
  ) {
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `brands?sinisterType=${availableSinister.type}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      availableSinister.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http.get(url, options).pipe(map(res => res as Brand[]));
  }

  getInterventions(
    policy: PolicyHome | PolicyOther,
    effectDate: string,
    selectedDate: string,
    availableSinister: AvailableSinister,
    isPrivateArea?: boolean
  ) {
    const url = this.setUrl(
      isPrivateArea,
      policy.policyNumber,
      `interventions/status?date=${selectedDate}&sinisterType=${availableSinister.type}&policyEffectDate=${effectDate}&riskModalityCode=${policy.riskModalityCode}`
    );
    const headers = this.setHeaders(isPrivateArea);
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      availableSinister.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    return this.http.get<Intervention>(url, options);
  }

  sendReport(
    policy: Policy,
    reportNew: PatrimonialSinister,
    isPrivateArea: boolean
  ) {
    const url = this.setUrl(isPrivateArea, policy.policyNumber, 'reports');
    const headers = this.setHeaders(isPrivateArea, {
      cacheResponse: false,
      clearCache: true
    });
    this.policiesCtx.clear();
    const params = this.or.setPatrimonialsOperationParams(
      policy,
      reportNew.type,
      isPrivateArea
    );
    const options = { ...headers, params };
    const { initTime, finishTime } = reportNew;
    const reportData = JSON.parse(JSON.stringify(reportNew));
    reportData.contactData.address.street += ` ${reportData.contactData.address.additionalInfo}`;
    const requestBody = JSON.stringify({
      ...reportData,
      ...(initTime && { initTime: moment(initTime).format('HH:mm') }),
      ...(finishTime && { finishTime: moment(finishTime).format('HH:mm') })
    });
    return this.http.post(url, requestBody, options);
  }

  publicPatrimonialsindetifyUser(
    user: PatrimonialIdentification,
    token: string
  ) {
    const url = this.getPublicPatrimonialsIdentificationUrl();
    const headers = this.base.getPublicPendingReceiptListHeaders(
      user.phoneNumber,
      token
    );
    let params = this.or.setPatrimonialsOperationParams(null, null, false);
    params = params.set('roDocumentNumber', user.documentNumber);
    if (user.iban) {
      params = params.set('iban', user.iban);
    }
    return this.http.get<PatrimonialUserValid>(url, { ...headers, params });
  }

  publicPatrimonialIdentificationPin(user: PatrimonialIdentification) {
    const pin = this.publicPatrimonialGetPin(user);
    return from(pin).pipe(
      flatMap(data => {
        if (data) {
          return this.http.post(data.url, data.requestBody, data.headers);
        } else {
          throwError('err');
        }
      }),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  publicPatrimonialIdentificationValidatePin(
    pinCode: string,
    phoneNumber: string
  ) {
    const data = this.publicPatrimonialValidatePinCode(pinCode, phoneNumber);
    return this.http.post(data.url, data.requestBody, data.headers);
  }

  saveIdentificationBlockedTimestamp(timestamp: string) {
    this.storage.set('PUBLIC_PATRIMONIALS_IDENTIFICATION', timestamp);
  }

  async getIdentificationBlockedTimestampStoraged() {
    await this.storage
      .get('PUBLIC_PATRIMONIALS_IDENTIFICATION')
      .then((data: string) => {
        this.identificationRetriesCounterTimeStamp = data;
      });
  }

  checkIdentificationBlockByTimeStampIsExpired() {
    let isExpired = true;
    const min = moment().diff(
      moment(this.identificationRetriesCounterTimeStamp),
      'minutes'
    );
    if (min < this.identificationRetriesBlock) {
      isExpired = false;
    } else {
      isExpired = true;
    }
    return isExpired;
  }

  private async publicPatrimonialGetPin(user: PatrimonialIdentification) {
    const pinUrl = this.mmobileHelper.getRequirePinUrl();
    const headers = this.base.getPublicHeaders();
    const versionEnv = Environment.config().versionEnv;
    const versionKey = versionEnv ? '1.1-' + versionEnv : '1.1';
    const params = this.or.setPatrimonialsOperationParams(null, null, false);
    const registerOperationContext = {
      roEntityCode: params.get(this.ENTITY_CODE),
      roGroupingCode: params.get(this.GROUPING_CODE),
      roOrigin: params.get(this.ORIGIN),
      roProductCode: params.get(this.PRODUCT_CODE),
      roProductId: params.get(this.PRODUCT_ID),
      roDocumentNumber: user.documentNumber
    };
    const body = {
      businessId: user.documentNumber,
      userType: SMS,
      userId: user.phoneNumber,
      appKey: this.publicPatrimonialsAppKey,
      versionKey,
      language: isAndroidApp(this.platform)
        ? LANGUAGE.android
        : LANGUAGE.default,
      validationData: {
        iban: user.iban || '',
        registerOperationContext
      }
    };
    return {
      url: pinUrl,
      headers,
      requestBody: body
    };
  }

  private publicPatrimonialValidatePinCode(
    pinCode: string,
    phoneNumber: string
  ) {
    const pinUrl = this.mmobileHelper.getValidatePinUrl();
    const headers = this.base.getPublicHeaders();
    const versionEnv = Environment.config().versionEnv;
    const versionKey = versionEnv ? '1.1-' + versionEnv : '1.1';
    const body = {
      pin: pinCode,
      userId: phoneNumber,
      appKey: this.publicPatrimonialsAppKey,
      versionKey
    };
    return {
      url: pinUrl,
      headers,
      requestBody: body
    };
  }

  private getPublicPatrimonialsIdentificationUrl() {
    const envConfig = Environment.config();
    const versionEnv = envConfig.versionEnv;
    const versionKey = versionEnv ? '1.1-' + versionEnv : '1.1';
    return `${envConfig.mmobileUrl}/services/p/${this.publicPatrimonialsAppKey}/${versionKey}/middlewareIdentification`;
  }

  private getPublicPatrimonialsUrl(policyNumber: string, url: string) {
    const envConfig = Environment.config();
    return `${
      envConfig.mmobileUrl
    }/services/j/mets/${this.mmobileHelper.getVersionId()}/middlewarePatrimonialsOpenPoliciesServices/${policyNumber}/${url}`;
  }

  private getPrivatePatrimonialsUrl(policyNumber: string, url: string) {
    return `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies/${policyNumber}/patrimonials/${url}`;
  }

  private mergeAvailableSinistersData = (
    availableSinister: AvailableSinister
  ) => {
    const sinisterLocalData = this.availableSinistersLocalData[
      availableSinister.type
    ];
    availableSinister.iconSrc = sinisterLocalData
      ? sinisterLocalData.iconSrc
      : this.defaultIconSrc;
    return availableSinister;
  };

  private mergeAvailableSinistersLocalData = (
    availableSinister: AvailableSinister
  ) => {
    if (this.availableSinistersData) {
      const sinisterData = this.availableSinistersData[availableSinister.type];
      if (sinisterData) {
        availableSinister.oldTypeCode = sinisterData.oldTypeCode;
        availableSinister.online = sinisterData.online;
        availableSinister.allowBricomercio = sinisterData.allowBricomercio;
        availableSinister.name = sinisterData.name;
        availableSinister.description = sinisterData.description;
      }
    }
    return availableSinister;
  };

  private loadDataFromMMobile() {
    this.availableSinistersData = this.mmobileHelper.getCustomConfigData(
      (this.auth.checkUserIsCompany()) ? 'availableBusinessSinistersData' : 'availableSinistersData'
    );
  }

  private setHeaders(
    isPrivateArea: boolean,
    opts?: { cacheResponse: boolean; clearCache?: boolean }
  ) {
    return isPrivateArea
      ? this.base.getPrivateOIMHeaders(opts)
      : this.base.getPublicHeadersWithToken(
          this.patrimonialContext.getPatrimonialJWT()
        );
  }

  private setUrl(isPrivateArea: boolean, policyNumber: string, url: string) {
    return isPrivateArea
      ? this.getPrivatePatrimonialsUrl(policyNumber, url)
      : this.getPublicPatrimonialsUrl(policyNumber, url);
  }
}
